import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { History, Calendar, User, Activity } from "lucide-react";
import { Id } from "@/convex/_generated/dataModel";
import { format } from "date-fns";
import { es } from "date-fns/locale";

export const Route = createFileRoute(
  "/_super/super/control-panel/$companyId/history",
)({
  component: CompanyHistory,
});

function CompanyHistory() {
  const { companyId } = Route.useParams();

  const details = useQuery(
    api.companies.subscriptions.queries.getCompanySubscriptionDetails,
    {
      companyId: companyId as Id<"companies">,
    },
  );

  if (!details) {
    return (
      <div className="space-y-6">
        <Card className="animate-pulse">
          <CardHeader>
            <div className="h-4 bg-muted rounded w-1/2" />
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="h-3 bg-muted rounded" />
              <div className="h-3 bg-muted rounded w-5/6" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { company } = details;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Historial de Actividad
          </CardTitle>
          <CardDescription>
            Registro de cambios y eventos para {company.name}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12">
            <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              Historial en desarrollo
            </h3>
            <p className="text-sm text-muted-foreground">
              El sistema de auditoría y registro de actividad estará disponible
              próximamente.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Información de la Empresa</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Creada el:</span>
              <span className="font-medium">
                {format(company._creationTime, "PPP", { locale: es })}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Activity className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Estado actual:</span>
              <Badge
                variant={company.status === "active" ? "default" : "secondary"}
              >
                {company.status === "active"
                  ? "Activa"
                  : company.status === "inactive"
                    ? "Inactiva"
                    : "Suspendida"}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Próximas Funcionalidades</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Registro de cambios en la suscripción</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Historial de asignación y remoción de usuarios</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Cambios en límites y configuración del plan</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Eventos de facturación y pagos</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Modificaciones en datos de la empresa</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Timeline visual de eventos</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
